import { TransactionPreviewStatus } from '@lmc-app/shared/enum/transaction-preview-status.enum';
import { LoanLookupBankAccount } from '@lmc-app/core/services/payment-processing.service';

/** Where the row's lookup is: not started, in flight, or finished (see `status`). */
export type ManualRowLookupState = 'idle' | 'loading' | 'done';

/** A dropdown entry: a looked-up account, or one typed in through the Custom option. */
export interface ManualBankAccount extends LoanLookupBankAccount {
  custom?: boolean;
}

export interface ManualTransactionRowModel {
  uid: string;
  loanNumber: string;
  payeeName: string;
  institutionCode: string;
  transitNumber: string;
  accountNumber: string;
  /** Customer's bank accounts from the lookup, offered in the Banking Account dropdown. */
  bankAccounts: ManualBankAccount[];
  /** Selected entry of `bankAccounts`; null when none is picked. */
  selectedBankAccount: ManualBankAccount | null;
  amount: number | null;
  paymentDate: Date | null;
  lookupState: ManualRowLookupState;
  /**
   * Lookup outcome, the same vocabulary the upload preview uses so both tables render a
   * Status + Note pair the same way. Null until the lookup returns. Only `Valid` may be
   * submitted.
   */
  status: TransactionPreviewStatus | null;
  /** Detail behind `status` — never a restatement of it. Shown in the Note column. */
  error: string | null;
  lastLookupKey: string | null;
  /** Per-row rejection returned by the submit call; also shown in the Note column. */
  rowError: string | null;
  /**
   * The promise to pay this row settles, when the dialog was opened from one. Sent with
   * the row so the submit links the two as the batch is written; null for a row typed
   * in here, which is the ordinary case.
   */
  promiseToPayId: number | null;
}

/** A row handed to the dialog already looked up — see ManualTransactionDialogData. */
export interface ManualTransactionPrefill {
  promiseToPayId?: number;
  loanNumber: string;
  payeeName: string;
  institutionCode: string;
  transitNumber: string;
  accountNumber: string;
  amount: number;
  paymentDate: string;
  status: TransactionPreviewStatus;
  error: string | null;
}

/** Display names for loanacct_ach.status (NLS); unmapped values fall back to the raw code. */
export const ACH_STATUS_LABELS: Readonly<Record<number, string>> = {
  0: 'Active',
  1: 'Disabled',
  2: 'Expired',
};

export function achStatusLabel(status: number | null): string {
  if (status == null) return '—';
  return ACH_STATUS_LABELS[status] ?? `Status ${status}`;
}

let _uidCounter = 0;
export function newRowUid(): string {
  _uidCounter += 1;
  return `row-${Date.now()}-${_uidCounter}`;
}

export function createEmptyRow(): ManualTransactionRowModel {
  return {
    uid: newRowUid(),
    loanNumber: '',
    payeeName: '',
    institutionCode: '',
    transitNumber: '',
    accountNumber: '',
    bankAccounts: [],
    selectedBankAccount: null,
    amount: null,
    paymentDate: null,
    lookupState: 'idle',
    status: null,
    error: null,
    lastLookupKey: null,
    rowError: null,
    promiseToPayId: null,
  };
}

/**
 * Builds a row from a lookup the caller already ran — the promise-to-pay bridge, which
 * gets its bank details and verdict from the same backend validation this dialog's own
 * lookup uses. `lastLookupKey` is set to the loan number so the row is not looked up
 * again the moment it renders.
 */
export function createPrefilledRow(
  prefill: ManualTransactionPrefill
): ManualTransactionRowModel {
  // The handed-in account becomes the only dropdown entry.
  const account: ManualBankAccount | null = prefill.institutionCode
    ? {
      institutionCode: prefill.institutionCode,
      transitNumber: prefill.transitNumber,
      accountNumber: prefill.accountNumber,
      loanNumber: prefill.loanNumber,
      achStatus: null,
      status: prefill.status,
      error: prefill.error,
    }
    : null;
  return {
    uid: newRowUid(),
    loanNumber: prefill.loanNumber,
    payeeName: prefill.payeeName,
    institutionCode: prefill.institutionCode,
    transitNumber: prefill.transitNumber,
    accountNumber: prefill.accountNumber,
    bankAccounts: account ? [account] : [],
    selectedBankAccount: account,
    amount: prefill.amount,
    paymentDate: new Date(`${prefill.paymentDate}T00:00:00`),
    lookupState: 'done',
    status: prefill.status,
    error: prefill.error,
    lastLookupKey: prefill.loanNumber,
    rowError: null,
    promiseToPayId: prefill.promiseToPayId ?? null,
  };
}
